/**
 * Pretty label + colour-coded icon for an AI provider.
 *
 * Internally we store the API-vendor key ("openai", "anthropic",
 * "gemini") because that's what each SDK expects. The UI surfaces the
 * product-facing name and brand colour ("ChatGPT" / "Claude" / "Gemini")
 * so the dashboard reads like the competitors users compare us to.
 */

const META = {
  openai: {
    name: "ChatGPT",
    color: "#10a37f",
    bg: "#d1faea",
  },
  anthropic: {
    name: "Claude",
    color: "#c96342",
    bg: "#fbe2d7",
  },
  gemini: {
    name: "Gemini",
    color: "#1f6feb",
    bg: "#dceaff",
  },
  perplexity: {
    name: "Perplexity",
    color: "#20808d",
    bg: "#d0eef1",
  },
  google_ai_overview: {
    name: "AI Overview",
    color: "#4285f4",
    bg: "#e3ebfd",
  },
};

const ICONS = {
  openai: (
    // Six-pointed sparkle (ChatGPT mark)
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855L13.105 8.39 15.124 7.22a.076.076 0 0 1 .071 0l4.83 2.79a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.412-.685zm2.01-3.027-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.682zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
    </svg>
  ),
  anthropic: (
    // Squared "A" (Claude logo)
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4.61 4h3.038l5.484 16h-3.038l-1.12-3.262H3.5L2.38 20H-.658L4.61 4zm.79 9.7h3.706l-1.853-5.39zm10.99-9.7h2.85l5.474 16h-3.038l-1.12-3.262h-5.473L13.16 20h-2.85L15.39 4zm.78 9.7h3.706l-1.853-5.39z" />
    </svg>
  ),
  gemini: (
    // Four-pointed sparkle (Gemini logo)
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2 L13.6 9.4 L21 11 L13.6 12.6 L12 20 L10.4 12.6 L3 11 L10.4 9.4 Z" />
    </svg>
  ),
  perplexity: (
    // Stylised "P" — concentric squares glyph evocative of Perplexity's mark
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 0h4v7h-4v-7z" />
    </svg>
  ),
  google_ai_overview: (
    // Four-colour Google "G" mark (single-path silhouette, tinted via
    // currentColor like the other badges rather than the real multi-
    // colour SVG, to keep this consistent with every other entry here).
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M21.6 12.23c0-.68-.06-1.36-.18-2.02H12v3.83h5.4a4.63 4.63 0 0 1-2 3.04v2.5h3.24c1.9-1.75 2.96-4.34 2.96-7.35z" />
      <path d="M12 22c2.7 0 4.97-.89 6.63-2.42l-3.24-2.5c-.9.6-2.06.96-3.39.96-2.6 0-4.8-1.76-5.59-4.12H3.06v2.59A10 10 0 0 0 12 22z" />
      <path d="M6.41 13.92a5.99 5.99 0 0 1 0-3.84V7.49H3.06a10 10 0 0 0 0 9.02l3.35-2.59z" />
      <path d="M12 5.98c1.47 0 2.79.5 3.83 1.5l2.87-2.87C16.96 2.99 14.7 2 12 2a10 10 0 0 0-8.94 5.49l3.35 2.59C7.2 7.74 9.4 5.98 12 5.98z" />
    </svg>
  ),
};

export function modelDisplayName(key) {
  return META[key]?.name ?? key;
}

export function modelColor(key) {
  return META[key]?.color ?? "#64748b";
}

/**
 * Inline pill — icon + product name. Pass `size="sm"` for table cells.
 */
export default function ModelBadge({ modelKey, size = "md", className = "" }) {
  const meta = META[modelKey];
  if (!meta) {
    return <span className={className}>{modelKey}</span>;
  }
  const sizeClasses =
    size === "sm"
      ? "text-xs gap-1 px-2 py-0.5"
      : "text-sm gap-1.5 px-2.5 py-1";
  const iconSize = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${sizeClasses} ${className}`}
      style={{ color: meta.color, backgroundColor: meta.bg }}
    >
      <span className={iconSize} aria-hidden="true">
        {ICONS[modelKey]}
      </span>
      {meta.name}
    </span>
  );
}
